/**
 * REST endpoint paths sanctioned outside GraphQL (ADR-0002): auth, account self-service, file
 * uploads. Every path is relative to `env.gatewayUrl` — the gateway is the only base URL the
 * frontend ever calls (docs/development/FrontendGuidelines.md §12.6).
 */
export const AUTH_ENDPOINTS = {
  register: '/auth/register',
  verifyEmail: '/auth/verify-email',
  resendVerification: '/auth/resend-verification',
  login: '/auth/login',
  google: '/auth/google',
  // Backend endpoint exists and is intentionally retained (provider-agnostic OAuth pipeline), but
  // LinkedIn is not surfaced in the UI as a product decision — nothing calls this today. Kept so the
  // endpoint map mirrors the REST surface, and so re-enabling is a UI change rather than a hunt.
  linkedin: '/auth/linkedin',
  refresh: '/auth/refresh',
  logout: '/auth/logout',
} as const;

export const ACCOUNT_ENDPOINTS = {
  me: '/account/me',
  changePassword: '/account/change-password',
  // Phone lives on the User aggregate, not TalentProfile, so it is Identity self-service (REST) and
  // not part of the GraphQL profile mutation.
  phone: '/account/phone',
  // Two-stage MFA enrollment (ADR-0006): setup creates a pending enrollment, verify activates it,
  // cancel discards it. MFA never activates until verify succeeds.
  mfaSetup: '/account/mfa/setup',
  mfaVerify: '/account/mfa/verify',
  mfaCancel: '/account/mfa/cancel',
  // Lost-authenticator reset. Two stages on purpose: the password proves knowledge, the emailed code
  // proves mailbox control, so a stolen password alone cannot strip MFA off an account.
  mfaResetRequest: '/account/mfa/reset/request',
  mfaResetConfirm: '/account/mfa/reset/confirm',
  forgotPassword: '/account/forgot-password',
  resetPassword: '/account/reset-password',
  // Pre-authentication lost-authenticator reset (BR-2/BR-5): for a caller who cannot sign in at all
  // (no code they have works), so the authenticated mfaResetRequest/mfaResetConfirm pair above is
  // unreachable. Same two-stage shape, keyed by email instead of a session.
  forgotMfa: '/account/forgot-mfa',
  resetMfa: '/account/reset-mfa',
  // Provider-based pre-auth recovery, for an account created through Google and so having no password
  // to confirm. `check` verifies the provider token and reports whether a recoverable account exists
  // WITHOUT touching it; `reset` is a second request the user has to confirm, and is the only one that
  // removes the authenticator. Neither takes an email — the account comes from the token's subject.
  oauthMfaRecoveryCheck: '/account/forgot-mfa/oauth/check',
  oauthMfaRecoveryReset: '/account/forgot-mfa/oauth/reset',
} as const;

/**
 * Admin Identity management (Platform User Directory). Identity is REST-only by design
 * (ADR-0002) — the directory is an administration view over these endpoints, not GraphQL.
 */
export const ADMIN_IDENTITY_ENDPOINTS = {
  users: '/admin/identity/users',
  userStats: '/admin/identity/users/stats',
  roles: '/admin/identity/roles',
  lock: '/admin/identity/lock',
  unlock: '/admin/identity/unlock',
  resetMfa: '/admin/identity/reset-mfa',
  deactivate: '/admin/identity/deactivate',
  reactivate: '/admin/identity/reactivate',
  /**
   * Platform staff tiers (ADR-0032). Super Admin only, enforced server-side; the console hides the
   * affordance for lesser tiers but the refusal is the API's. Grant replaces whichever tier the target
   * held; revoke takes no tier name because a user holds at most one.
   */
  grantPlatformRole: '/admin/identity/platform-role/grant',
  revokePlatformRole: '/admin/identity/platform-role/revoke',
  /** The persisted privilege trail (ADR-0033). Super Admin only — a lesser tier gets 403. */
  platformRoleAudit: '/admin/identity/platform-role/audit',
} as const;

/**
 * Applications REST. GraphQL owns the module's reads and writes (ADR-0002); a resume is the
 * documented exception, because a byte stream is not a GraphQL response. The endpoint authorizes the
 * caller itself — the owning candidate, or a member of the hiring company — so the same URL serves
 * both portals and neither has to know the other's rules.
 */
export const APPLICATION_ENDPOINTS = {
  resume: (applicationId: string) => `/applications/${applicationId}/resume`,
} as const;

/**
 * Company-facing candidate resume download (Candidate Access Policy, V1).
 * Separate from `APPLICATION_ENDPOINTS.resume` — this one is membership-authorized and covers the
 * new Search Candidates screen, not an application's submitted resume.
 */
export const COMPANY_CANDIDATE_ENDPOINTS = {
  resumeDownload: (companyId: string, candidateId: string, resumeId: string) =>
    `/company/${companyId}/candidates/${candidateId}/resumes/${resumeId}/download`,
} as const;

/** Platform administration outside Identity. Admin-gated on the server (ADR-0010 role check). */
export const ADMIN_PLATFORM_ENDPOINTS = {
  /** Component-level dependency health. NOT `/health` — that one is the container's liveness ping. */
  systemHealth: '/admin/system-health',
} as const;

/**
 * Published legal documents (ADR-0026). Anonymous and REST rather than GraphQL because the
 * registration form reads it before any session exists, alongside the pre-authentication `/auth/*`
 * surface — see `LegalDocumentsController` for the recorded reasoning.
 */
export const LEGAL_ENDPOINTS = {
  /** Everything published, plus the two documents accepted at registration named explicitly. */
  current: '/legal-documents/current',
  /**
   * One document by slug. The slug comes from the API (`LegalDocument.slug`) rather than being built
   * here — the server owns the mapping from document type to URL segment, so a new document type is
   * linkable without a frontend release.
   */
  bySlug: (slug: string) => `/legal-documents/${encodeURIComponent(slug)}`,
} as const;
